import * as React from "react"
import { Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, TextField, Tooltip, debounce } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { AXIOS_API_INSTANCE, get_service_order_part_color } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import SearchIcon from '@mui/icons-material/Search';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import InfoIcon from '@mui/icons-material/Info';
import onFilterChange from "../../Utils/OnFilterChange"
import { useSnackbar } from 'notistack';
import { DataGrid, } from "@mui/x-data-grid"
import { SimpleCustomToolbar } from "../CustomToolbar"
import { useNavigate } from "react-router-dom";
import DocumentTitle from "../../Utils/DocumentTitle";
import { formatDurationDays, moneyFormat, widthChangeEvent } from "../../Utils"
import { useTrackedLocation, useTrackedUser } from "../UserState"
import { serviceOrderChip, sort_custom_statuses } from "../../Utils/service"
import dayjs from "dayjs"
import ServiceOrderPartStatusDropdown from "./ServiceOrderPartStatusDropdown"
import { ServiceOrderStatusFilter } from "./ServiceOrders"

const get_included_statuses = (my_user) => {
    return sort_custom_statuses(my_user.client.service_order_parts_statuses).filter((status) => {
        return ['CREATED', 'ACQUIRING'].includes(my_user.client.service_order_parts_statuses[status].type)
    })
}

const get_included_service_order_statuses = (my_user) => {
    return sort_custom_statuses(my_user.client.service_order_statuses).filter((status) => {
        return ['CREATED', 'IN_PROGRESS'].includes(my_user.client.service_order_statuses[status].type)
    })
}

export default function ServiceOrderParts() {
    DocumentTitle('Service Order Parts')
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const [selectedLocation,] = useTrackedLocation();
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`serviceorderparts:pageSize`) || 10),
        page: 0,
    });
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0, first_render: true })
    const [sorting, setSorting] = React.useState({ field: 'date_created', sort: 'desc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(true)
    const [serviceOrderPartSearch, setServiceOrderPartSearch] = React.useState('')
    const [filterForApproved, setFilterForApproved] = React.useState(true)
    const [includeStatuses, setIncludeStatuses] = React.useState(localStorage.getItem(`serviceorderparts:status_filters`) ? localStorage.getItem(`serviceorderparts:status_filters`).split(',') : get_included_statuses(my_user))
    const [newIncludeStatuses, setNewIncludeStatuses] = React.useState(localStorage.getItem(`serviceorderparts:status_filters`) ? localStorage.getItem(`serviceorderparts:status_filters`).split(',') : get_included_statuses(my_user))
    const [includeServiceOrderStatuses, setIncludeServiceOrderStatuses] = React.useState(localStorage.getItem(`serviceorderparts:so_status_filters`) ? localStorage.getItem(`serviceorderparts:so_status_filters`).split(',') : get_included_service_order_statuses(my_user))

    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)
    const navigate = useNavigate();

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 150,
            flex: 1,
            sortable: true,
            valueFormatter: (value) => value || "",
            renderCell: (params) => {
                return (
                    <Box>
                        <span>
                            {params.value}
                            {params.row.service_order_warranties_association.length > 0 &&
                                <Tooltip title={`Part has warranty claim`}>
                                    <FolderSpecialIcon sx={{ height: '100%', verticalAlign: 'middle' }} />
                                </Tooltip>
                            }
                        </span>
                    </Box>
                )
            }
        },
        {
            field: 'description',
            headerName: 'Description',
            minWidth: 150,
            flex: 1,
            sortable: true,
        },
        {
            field: 'sku',
            headerName: 'SKU',
            minWidth: 100,
            flex: 1,
            sortable: true,
            filterable: true,
        },
        {
            field: "cost",
            headerName: "Price",
            width: 125,
            editable: false,
            sortable: true,
            filterable: false,
            valueFormatter: (value) => `$${moneyFormat(value ?? "")}`
        },
        {
            field: "status_name",
            headerName: "Status",
            width: 175,
            editable: false,
            filterable: false,
            cellClassName: 'datagrid-no-padding',
            renderCell: (params) => <Box
                sx={{
                    width: '100%',
                    alignItems: 'center',
                    height: '100%',
                    containerType: 'normal'
                }}
            >
                {['CREATED', 'IN_PROGRESS', 'WORK_COMPLETE'].includes(params.row.service_order.status_type) && !['INSTALLED', 'DECLINED'].includes(params.row.status_type) ?
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                        {my_user.client.service_order_parts_statuses[params.row.status_name].alert_hours > 0 &&
                            dayjs().diff(dayjs.utc(params.row.last_status_change), 'hours') >= my_user.client.service_order_parts_statuses[params.row.status_name].alert_hours &&
                            <Tooltip title={`No status change in ${formatDurationDays(dayjs().diff(dayjs.utc(params.row.last_status_change), 'hours'))}`}>
                                <InfoIcon sx={{ height: '20px', verticalAlign: 'text-bottom' }} color="error" />
                            </Tooltip>
                        }
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', flexGrow: 1 }}>
                            <ServiceOrderPartStatusDropdown
                                serviceOrder={params.row.service_order}
                                service_id={params.row.service_order_service_id}
                                part={params.row}
                                include_declined={false}
                                include_installed={false}
                                updatedPartCallback={(part) => {
                                    const index = rowData.rows.findIndex((x) => x.id === part.id)
                                    const new_rowData = { ...rowData }
                                    new_rowData.rows = [...new_rowData.rows]
                                    new_rowData.rows[index].status_name = part.status_name
                                    new_rowData.rows[index].status_type = part.status_type
                                    new_rowData.rows[index].last_status_change = part.last_status_change
                                    setRowData(new_rowData)
                                }
                                }
                            />
                        </Box>
                    </Box>
                    :
                    <Chip label={params.value} color={get_service_order_part_color(my_user.client.service_order_parts_statuses, params.value)} sx={{ ml: 1 }} />
                }
            </Box>
        },
        // {
        //     field: 'service_order_service:date_created', //TODO: Add correct date
        //     headerName: 'Date Approved',
        //     type: 'date',
        //     flex: 1,
        //     sortable: true,
        //     filterable: false,
        //     valueGetter: (value, row) => dayjs.utc(row.service_order_service.date_created).toDate(),
        // },
        {
            field: 'service_order_id',
            headerName: 'Service Order',
            minWidth: 170,
            flex: 1,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                return (<Button onClick={(e) => { e.preventDefault(); navigate(`/ServiceOrders/${params.row.service_order_id}`) }} href={`/ServiceOrders/${params.row.service_order_id}`}>Service Order #{params.row.service_order_id}</Button>)
            }
        },
        {
            field: 'service_order:status_name',
            headerName: 'Service Order Status',
            width: 200,
            editable: false,
            filterable: false,
            sortable: true,
            display: 'flex',
            valueGetter: (value, row) => row.service_order.status_name,
            renderCell: (params) => (
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {serviceOrderChip(my_user, params.row.service_order.status_name, params.row.service_order.status_type)}
                </Box>
            )
        },
        {
            field: 'service_order_service:name',
            headerName: 'Service',
            minWidth: 100,
            flex: 1,
            sortable: true,
            filterable: true,
            valueGetter: (value, row) => row.service_order_service.name,
        },
        {
            field: 'service_order_service:customer_approved_date',
            headerName: 'Service Approval Date',
            type: 'dateTime',
            minWidth: 100,
            flex: 1,
            sortable: false,
            filterable: false,
            valueGetter: (value, row) => row.service_order_service.customer_approved_date ? dayjs.utc(row.service_order_service.customer_approved_date).toDate() : null,
        },
    ]

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`serviceorderparts:pageSize`, model.pageSize)
        setPaginationModel(model)
    }

    const updateServiceOrderPartSearch = (event) => {
        setServiceOrderPartSearch(event.target.value.trim())
        if (event.target.value.trim() !== serviceOrderPartSearch) {
            debouncedGetServiceOrderParts(getServiceOrderParts, paginationModel, sorting.field, sorting.sort, filtering, event.target.value.trim(), includeServiceOrderStatuses, filterForApproved)
        }
    }

    const getServiceOrderParts = (paginationModel, sortField, sortDirection, filterVar, serviceOrderPartSearch, includeServiceOrderStatuses, filterForApproved) => {
        setIsLoading(true)
        let my_filters = { "and": [] }
        if (Object.keys(filterVar).length > 0) {
            my_filters["and"].push(filterVar)
        }
        if (serviceOrderPartSearch.length > 0) {
            my_filters['and'].push({ 'sku.ilike': serviceOrderPartSearch })
        }
        if (filterForApproved) {
            my_filters['and'].push({ 'service_order_service:customer_approved_status.eq': 'APPROVED' })
        }
        my_filters["and"].unshift({ 'or': [] })
        includeStatuses.forEach((status) => {
            my_filters["and"][0]['or'].push({ 'status_name.eq': status })
        })
        if (includeServiceOrderStatuses.length > 0 && includeServiceOrderStatuses.length !== Object.keys(my_user.client.service_order_statuses).length) {
            my_filters["and"].unshift({ 'or': [] })
            includeServiceOrderStatuses.forEach((status => {
                my_filters["and"][0]['or'].push({ 'service_order:status_name.eq': status })
            }))
        }
        if (selectedLocation.id !== "all") {
            my_filters['and'].push({ 'service_order:current_location_id.eq': selectedLocation.id })
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("serviceorderparts", {
                params: {
                    page_num: paginationModel.page,
                    page_size: paginationModel.pageSize,
                    order_by: sortField,
                    order_by_direction: sortDirection,
                    filters: JSON.stringify(my_filters),
                },
            })
                .then((serviceorderparts) => {
                    setRowData({
                        rows: serviceorderparts.data.data,
                        total_count: serviceorderparts.data.total_count,
                    })
                    setIsLoading(false)
                }
                ).catch((error) => {
                    enqueueSnackbar(`Failed to load service order parts: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                    setIsLoading(false)
                })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetServiceOrderParts = React.useMemo(() => debounce((getServiceOrderParts, paginationModel, sortField, sortDirection, filterVar, serviceOrderPartSearch, includeServiceOrderStatuses, filterForApproved) => getServiceOrderParts(paginationModel, sortField, sortDirection, filterVar, serviceOrderPartSearch, includeServiceOrderStatuses, filterForApproved), 400), []);

    React.useEffect(() => {
        getServiceOrderParts(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderPartSearch, includeServiceOrderStatuses, filterForApproved)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, sorting, selectedLocation, includeStatuses, includeServiceOrderStatuses, filterForApproved])
    React.useEffect(() => {
        if (!rowData.first_render) {
            debouncedGetServiceOrderParts(getServiceOrderParts, paginationModel, sorting.field, sorting.sort, filtering, serviceOrderPartSearch, includeServiceOrderStatuses, filterForApproved)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtering])

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    return (
        (<Box sx={{ width: '100%' }}>
            <Grid container spacing={1} alignItems={'flex-start'}>
                <Grid container columnSpacing={1} sx={{ mt: 0.5, mb: 0.5 }} direction="row" justifyContent="space-between" alignItems="center" size={12}>
                    <Grid size={2}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="contractsearch"
                            label="Search SKU"
                            type="text"
                            variant="filled"
                            disabled={isLoading}
                            value={serviceOrderPartSearch}
                            onChange={updateServiceOrderPartSearch}
                            slotProps={{
                                input: {
                                    inputProps: { min: 1 },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {<SearchIcon />}
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Tooltip
                            title="View only parts that have been approved by the customer"
                            arrow
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterForApproved}
                                        onChange={(event) => setFilterForApproved(event.target.checked)}
                                        disabled={isLoading}
                                    />
                                }
                                label="Only Approved"
                            />
                        </Tooltip>
                    </Grid>
                    <Grid size={4}>
                        <ServiceOrderStatusFilter
                            statuses={sort_custom_statuses(my_user.client.service_order_statuses)}
                            includeStatuses={includeServiceOrderStatuses}
                            setIncludeStatuses={setIncludeServiceOrderStatuses}
                            showServiceOrderStats={false}
                            isLoading={isLoading}
                            localStorageLocation="serviceorderparts:so_status_filters"
                        />
                    </Grid>
                    <Grid size={4} container alignItems="center">
                        <FormControl sx={{ m: 1, width: '100%' }}>
                            <InputLabel id="serviceorder-part-statuses-filter-checkbox-label">Part Status Filter</InputLabel>
                            <Select
                                labelId="serviceorder-part-statuses-filter-checkbox-label"
                                id="serviceorder-part-statuses-filter-checkbox"
                                multiple
                                value={sort_custom_statuses(my_user.client.service_order_parts_statuses).filter((x) => newIncludeStatuses.includes(x))}
                                onChange={(e) => setNewIncludeStatuses(e.target.value)}
                                onClose={(e) => {
                                    if (JSON.stringify(newIncludeStatuses.sort()) !== JSON.stringify(includeStatuses.sort())) {
                                        setIncludeStatuses(newIncludeStatuses)
                                        localStorage.setItem(`serviceorderparts:status_filters`, newIncludeStatuses)
                                    }
                                }}
                                disabled={isLoading}
                                input={<OutlinedInput id="select-multiple-chip" label="Part Status Filter" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((status) => (
                                            <Chip key={status} label={status} color={get_service_order_part_color(my_user.client.service_order_parts_statuses, status)} />
                                        ))}
                                    </Box>
                                )}
                            >
                                {sort_custom_statuses(my_user.client.service_order_parts_statuses).map((status) => (
                                    <MenuItem key={status} value={status}>
                                        <Checkbox checked={newIncludeStatuses.includes(status)} />
                                        <Chip label={status} color={get_service_order_part_color(my_user.client.service_order_parts_statuses, status)} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <DataGrid
                        key={windowInnerWidth}
                        slots={{
                            toolbar: () => SimpleCustomToolbar(),
                        }}
                        rows={rowData.rows}
                        rowCount={rowData.total_count}
                        columns={columns}
                        disableRowSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                        autoHeight={true}
                        filterMode={"server"}
                        loading={isLoading}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setMyPaginationModel}
                        // onRowClick={(params) => setSelectedPart(params.row)}
                        sortingMode="server"
                        onSortModelChange={(params) => {
                            if (params[0] !== undefined) {
                                setSorting(params[0])
                            } else {
                                setSorting({ field: 'id', sort: 'asc' })
                            }
                        }}
                        onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns status and traderName, the other columns will remain visible
                                    'service_order_service:customer_approved_date': false,
                                },
                            },
                        }}
                    />
                </Grid>
            </Grid>
        </Box>)
    );
}